'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { getTutorialUiContent } from '@/lib/ui-content/tutorial-ui-content';

type SpotlightPosition = 'top' | 'bottom' | 'left' | 'right';

interface SpotlightTarget {
  selector: string;
  title: string;
  description: string;
  position?: SpotlightPosition;
  padding?: number;
}

interface TutorialSpotlightProps {
  locale: LocaleDetails;
  targets: SpotlightTarget[];
  currentStep: number;
  onNext: () => void;
  onSkip: () => void;
  onComplete: () => void;
}

export const TutorialSpotlight = ({
  locale,
  targets,
  currentStep,
  onNext,
  onSkip,
  onComplete,
}: TutorialSpotlightProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Refs for direct DOM manipulation
  const maskRectRef = useRef<SVGRectElement>(null);
  const borderRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const calloutRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);

  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rafRef = useRef<number | null>(null);
  const tutorialContent = getTutorialUiContent(locale);

  const target = targets[currentStep];
  const isLastStep = currentStep === targets.length - 1;

  // Set mounted state
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cleanup refs on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Core update function - Directly manipulates DOM
  const updatePositions = useCallback(() => {
    if (!target) return;

    const element = document.querySelector(target.selector);
    if (!element) return;

    // 1. Get measurements
    const rect = element.getBoundingClientRect();
    const padding = target.padding ?? 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // 2. Calculate Spotlight Geometry (Clamped)
    const minMargin = 2;
    let slLeft = rect.left - padding;
    let slTop = rect.top - padding;
    let slWidth = rect.width + padding * 2;
    let slHeight = rect.height + padding * 2;

    // Clamp values
    if (slLeft < minMargin) {
      slWidth -= (minMargin - slLeft);
      slLeft = minMargin;
    }
    if (slLeft + slWidth > viewportWidth - minMargin) {
      slWidth = viewportWidth - slLeft - minMargin;
    }
    if (slTop < minMargin) {
      slHeight -= (minMargin - slTop);
      slTop = minMargin;
    }
    if (slTop + slHeight > viewportHeight - minMargin) {
      slHeight = viewportHeight - slTop - minMargin;
    }

    // Ensure non-negative dimensions
    slWidth = Math.max(slWidth, 0);
    slHeight = Math.max(slHeight, 0);

    // 3. Apply to Spotlight Elements (Mask, Border, Glow)
    if (maskRectRef.current) {
      maskRectRef.current.setAttribute('x', String(slLeft));
      maskRectRef.current.setAttribute('y', String(slTop));
      maskRectRef.current.setAttribute('width', String(slWidth));
      maskRectRef.current.setAttribute('height', String(slHeight));
    }

    const updateDiv = (ref: React.RefObject<HTMLDivElement>, offset = 0) => {
      if (ref.current) {
        ref.current.style.transform = `translate(${slLeft - offset}px, ${slTop - offset}px)`;
        ref.current.style.width = `${slWidth + offset * 2}px`;
        ref.current.style.height = `${slHeight + offset * 2}px`;
      }
    };

    updateDiv(borderRef, 0);
    updateDiv(glowRef, 4);

    // 4. Calculate & Apply Callout Position
    if (calloutRef.current) {
      const calloutWidth = calloutRef.current.offsetWidth || (isMobile ? Math.min(320, window.innerWidth - 32) : 320);
      const calloutHeight = calloutRef.current.offsetHeight || 200;
      const offset = 20;

      let posLeft = 0;
      let posTop = 0;
      let actualPosition = target.position ?? 'bottom';

      if (isMobile) {
        // Mobile: Fixed at bottom
        calloutRef.current.style.bottom = '16px';
        calloutRef.current.style.left = '16px';
        calloutRef.current.style.right = '16px';
        calloutRef.current.style.top = 'auto';
        calloutRef.current.style.width = 'auto';
        calloutRef.current.style.transform = 'none';

        // Hide arrow on mobile
        if (arrowRef.current) arrowRef.current.style.display = 'none';
        return;
      }

      // Desktop positioning logic
      calloutRef.current.style.width = '320px';
      calloutRef.current.style.right = 'auto'; // Reset right/bottom from mobile styles
      calloutRef.current.style.bottom = 'auto';

      const spaceAbove = slTop - offset;
      const spaceBelow = viewportHeight - (slTop + slHeight) - offset;
      const spaceLeft = slLeft - offset;
      const spaceRight = viewportWidth - (slLeft + slWidth) - offset;

      // Smart auto-positioning
      if (actualPosition === 'top' && spaceAbove < calloutHeight) {
        actualPosition = spaceBelow >= calloutHeight ? 'bottom' : (spaceRight >= calloutWidth ? 'right' : 'left');
      } else if (actualPosition === 'bottom' && spaceBelow < calloutHeight) {
        actualPosition = spaceAbove >= calloutHeight ? 'top' : (spaceRight >= calloutWidth ? 'right' : 'left');
      } else if (actualPosition === 'left' && spaceLeft < calloutWidth) {
        actualPosition = spaceRight >= calloutWidth ? 'right' : (spaceBelow >= calloutHeight ? 'bottom' : 'top');
      } else if (actualPosition === 'right' && spaceRight < calloutWidth) {
        actualPosition = spaceLeft >= calloutWidth ? 'left' : (spaceBelow >= calloutHeight ? 'bottom' : 'top');
      }

      const centerX = Math.max(16, Math.min(viewportWidth - calloutWidth - 16, slLeft + slWidth / 2 - calloutWidth / 2));
      const centerY = Math.max(16, Math.min(viewportHeight - calloutHeight - 16, slTop + slHeight / 2 - calloutHeight / 2));

      switch (actualPosition) {
        case 'top':
          posTop = Math.max(16, slTop - calloutHeight - offset);
          posLeft = centerX;
          break;
        case 'bottom':
          posTop = slTop + slHeight + offset;
          posLeft = centerX;
          break;
        case 'left':
          posTop = centerY;
          posLeft = Math.max(16, slLeft - calloutWidth - offset);
          break;
        case 'right':
          posTop = centerY;
          posLeft = slLeft + slWidth + offset;
          break;
      }

      calloutRef.current.style.top = `${posTop}px`;
      calloutRef.current.style.left = `${posLeft}px`;
      calloutRef.current.style.transform = 'none'; // Clear any prev transforms

      // Update Arrow
      if (arrowRef.current) {
        arrowRef.current.style.display = 'block';
        // Reset classes
        arrowRef.current.className = 'absolute border-8';

        // Add directional classes
        let arrowClass = '';
        switch (actualPosition) {
          case 'top':
            arrowClass = 'bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-t-white dark:border-t-dark border-l-transparent border-r-transparent border-b-transparent';
            break;
          case 'bottom':
            arrowClass = 'top-0 left-1/2 -translate-x-1/2 -translate-y-full border-b-white dark:border-b-dark border-l-transparent border-r-transparent border-t-transparent';
            break;
          case 'left':
            arrowClass = 'right-0 top-1/2 -translate-y-1/2 translate-x-full border-l-white dark:border-l-dark border-t-transparent border-b-transparent border-r-transparent';
            break;
          case 'right':
            arrowClass = 'left-0 top-1/2 -translate-y-1/2 -translate-x-full border-r-white dark:border-r-dark border-t-transparent border-b-transparent border-l-transparent';
            break;
        }
        arrowRef.current.className = `absolute border-8 ${arrowClass}`;
      }
    }
  }, [target, isMobile]);

  // Handle Scroll and Resize with RAF
  useEffect(() => {
    let ticking = false;

    const onScrollOrResize = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updatePositions();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, { passive: true, capture: true }); // Capture to catch bubble-up scroll events

    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, { capture: true });
    };
  }, [updatePositions]);

  // Initial Visibility and Positioning logic
  const performScroll = useCallback(() => {
    if (!target) return;
    const element = document.querySelector(target.selector);
    if (!element) return;

    if (isMobile) {
      // Mobile: Instant, snappy alignment
      const elementRect = element.getBoundingClientRect();
      // Target center screen minus some offset for the bottom sheet
      const targetY = window.scrollY + elementRect.top - (window.innerHeight - 250) / 2 + elementRect.height / 2;

      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: 'auto'
      });
    } else {
      // Desktop: Smooth
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Force immediate update after scroll command
    requestAnimationFrame(updatePositions);

    // Check for scroll end
    let lastY = window.scrollY;
    const checkScrollEnd = () => {
      if (Math.abs(window.scrollY - lastY) < 1) {
        updatePositions(); // Final adjustment
      } else {
        lastY = window.scrollY;
        rafRef.current = requestAnimationFrame(checkScrollEnd);
      }
    };
    rafRef.current = requestAnimationFrame(checkScrollEnd);

  }, [target, isMobile, updatePositions]);


  useEffect(() => {
    setIsVisible(false);
    setIsTransitioning(true);

    const initTimer = setTimeout(() => {
      performScroll();
      // Short delay before fading in
      setTimeout(() => setIsVisible(true), isMobile ? 50 : 150);
      setIsTransitioning(false);
    }, 50);

    return () => clearTimeout(initTimer);
  }, [currentStep, performScroll, isMobile]);


  const handleNext = () => {
    setIsVisible(false);
    setIsTransitioning(true);
    setTimeout(() => {
      if (isLastStep) {
        onComplete();
      } else {
        onNext();
      }
    }, isMobile ? 100 : 200);
  };

  if (!target || !mounted) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[5000] transition-opacity ${isMobile ? 'duration-150' : 'duration-300'} ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleNext}
    >
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 h-full w-full pointer-events-none">
        <defs>
          <mask id={`spotlight-mask-${currentStep}`}>
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {/* Cutout controlled by Ref */}
            <rect
              ref={maskRectRef}
              rx="12"
              ry="12"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.5)"
          className="dark:fill-black/60"
          mask={`url(#spotlight-mask-${currentStep})`}
        />
      </svg>

      {/* Spotlight ring/border */}
      <div
        ref={borderRef}
        className={`absolute top-0 left-0 rounded-xl border-2 border-[#00a798] shadow-[0_0_0_4px_rgba(0,167,152,0.2),0_0_20px_rgba(0,167,152,0.3)] transition-opacity ${isMobile ? 'duration-100' : 'duration-300'} pointer-events-none`}
      // style props removed, handled by ref
      />

      {/* Subtle pulsing glow animation */}
      <div
        ref={glowRef}
        className="absolute top-0 left-0 rounded-xl border border-[#00a798]/50 animate-pulse pointer-events-none"
      />

      {/* Callout box */}
      <div
        ref={calloutRef}
        className={`absolute rounded-2xl bg-white p-5 shadow-xl backdrop-blur-sm dark:bg-dark dark:border dark:border-transparent-dark transition-opacity ${isMobile ? 'duration-100' : 'duration-300'} pointer-events-auto`}
        onClick={(e) => e.stopPropagation()}
        style={{ opacity: isVisible ? 1 : 0 }}
      >
        {/* Arrow pointing to target (desktop only) */}
        <div
          ref={arrowRef}
          className="hidden" // Class toggled in JS
        />

        {/* Step indicator */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#00a798] text-xs font-bold text-white shadow-sm">
            {currentStep + 1}
          </div>
          <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
            {tutorialContent.stepIndicator
              .replace('{current}', String(currentStep + 1))
              .replace('{total}', String(targets.length))}
          </span>

          {/* Progress bar */}
          <div className="ml-auto flex gap-1">
            {targets.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all duration-300 ${index === currentStep
                  ? 'w-5 bg-[#00a798]'
                  : index < currentStep
                    ? 'w-1.5 bg-[#00a798]/40'
                    : 'w-1.5 bg-neutral-200 dark:bg-neutral-600'
                  }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <h3 className="mb-2 text-lg font-bold text-neutral-900 dark:text-white">
          {target.title}
        </h3>
        <p className="mb-5 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
          {target.description}
        </p>

        {/* Actions */}
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSkip();
            }}
            className="px-3 py-2 text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
          >
            {tutorialContent.skipButton}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="flex-1 max-w-[160px] rounded-xl bg-[#00a798] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#008b7a] hover:shadow-lg active:scale-[0.98]"
          >
            {isLastStep ? tutorialContent.gotIt : tutorialContent.nextButton}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
